import { useSnapshot } from "valtio";
import { workflowStore } from "../../workflow/workflowStore";
import { getWorkflow, getWorkflowDisplayGroups } from "../../workflow/workflowItems";
import { useWorkflowAccess } from "../../workflow/useWorkflowAccess";
import "./workflowOverview.scss";

type Props = {
  engagementType: string;
  onNavigate: (slug: string) => void;
};

export function WorkflowOverview({ engagementType, onNavigate }: Props) {
  const canSee = useWorkflowAccess();
  const workflowSnap = useSnapshot(workflowStore);
  const stages = getWorkflow(engagementType);

  const progressItems = stages
    .filter((stage) => stage.countsTowardProgress)
    .flatMap((stage) => stage.items);
  const completeCount = progressItems.filter(
    (i) => workflowSnap.statusByKey[i.key]?.status === "complete"
  ).length;
  const nextItem = progressItems.find(
    (i) => workflowSnap.statusByKey[i.key]?.status === "incomplete"
  );

  return (
    <div className="workflow-overview">
      <div className="overview-head">
        <h5>Overview</h5>
        {!workflowSnap.loadFailed && (
          <span className="head-meta">
            {completeCount} of {progressItems.length} complete
            {nextItem && (
              <>
                {" · next: "}
                <button className="next-link" onClick={() => onNavigate(nextItem.slug)}>
                  {nextItem.name}
                </button>
              </>
            )}
          </span>
        )}
      </div>

      {workflowSnap.loadFailed && (
        <div className="alert alert-warning py-2">
          Setup status is unavailable right now — all items below are still accessible.
        </div>
      )}

      {getWorkflowDisplayGroups(engagementType)
        .map((group) => ({ ...group, items: group.items.filter(canSee) }))
        .filter((group) => group.items.length)
        .map((group, groupIndex) => (
        <div key={group.key} className="overview-stage">
          <div className="stage-label">
            {group.numbered ? `${groupIndex + 1} · ` : ""}
            {group.name}
          </div>
          <div className="stage-card">
            {group.items.map((workflowItem) => {
              const status = workflowSnap.statusByKey[workflowItem.key];
              const isComplete = status?.status === "complete";
              // Set up but missing data. Marked with "!" rather than colour alone so it
              // can't be confused with the amber "up next" highlight.
              const needsAttention = status?.status === "attention";
              const isNext = workflowItem.key === nextItem?.key;
              const mark = isComplete ? "✓" : needsAttention ? "!" : "";
              return (
                <button
                  key={workflowItem.key}
                  className={`overview-row ${isNext ? "next" : ""}${
                    needsAttention ? " attention" : ""
                  }`}
                  onClick={() => onNavigate(workflowItem.slug)}
                >
                  <span
                    className={`check ${
                      isComplete ? "done" : needsAttention ? "attention" : isNext ? "up-next" : "open"
                    }`}
                  >
                    {mark}
                  </span>
                  <span
                    className={`item-name ${
                      !isComplete && !isNext && !needsAttention ? "muted" : ""
                    }`}
                  >
                    {workflowItem.name}
                  </span>
                  <span className="item-summary">{status?.summary ?? ""}</span>
                  {isNext ? <span className="go">Start →</span> : <span className="chev">›</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
